/**
 * Renewal Engine — domain module.
 *
 * Segundo motor activado (Sprint 11D), siguiendo exactamente el patrón de
 * recovery.js (CLAUDE_CODE_README.md §7: "Extrae el stub, implementa los
 * mismos métodos, cambia status a live, wire en container.js"). Cero
 * cambios de UI — EngineView, Home, Sidebar ya son genéricos.
 *
 * Diferencia con Recovery: la señal de urgencia no es inactividad, es
 * cercanía a la fecha de renovación (`verticalAttributes.daysUntilRenewal`,
 * calculado por Ingestion.jsx a partir de la columna `renewal_date` del CSV).
 * Un actor sin esa señal simplemente no es candidato de renovación — no se
 * le fabrica una oportunidad.
 */
import { createOpportunity, createClosure, createOutcome } from "../entities";

const ENGINE_ID = "renewal";

/**
 * @param {{
 *   storage: import('../ports').StoragePort,
 *   intelligence: import('../ports').IntelligencePort,
 *   llm: import('../ports').LLMPort,
 *   knowledge: import('../ports').KnowledgePort,
 *   crm: import('../ports').CRMPort,
 * }} deps
 */
export function createRenewalEngine(deps) {
  const { storage, intelligence, llm, knowledge, crm } = deps;

  return {
    id: ENGINE_ID,

    async evaluateActor(actor) {
      const daysUntilRenewal = actor.verticalAttributes?.daysUntilRenewal;
      // Sin fecha de renovación real, este actor no es candidato — no se
      // inventa una oportunidad donde no hay dato que la sostenga.
      if (daysUntilRenewal == null || daysUntilRenewal < 0) return;

      const signals = await storage.listSignals(actor.id);
      const { score, priority, probability, windowDays } = await intelligence.scoreActor({
        actor, signals, engineId: ENGINE_ID, vertical: actor.vertical,
      });
      const estimatedValue = await intelligence.estimateValue({
        actor, engineId: ENGINE_ID, vertical: actor.vertical, probability,
      });
      // ID determinístico por actor+motor (Sprint QA, agosto 2026): antes,
      // cada reimportación generaba una oportunidad NUEVA (uuid aleatorio)
      // aunque el actor fuera el mismo, así que la interfaz seguía mostrando
      // oportunidades viejas y huérfanas mientras el vínculo real con GHL
      // vivía solo en la más reciente. Con actor.id ya estable (ver
      // ingestionParsing.js), reevaluar el mismo actor ahora actualiza la
      // MISMA oportunidad en vez de crear otra. Se preserva responseStatus/
      // outcome/notes de la oportunidad existente si ya tenía progreso real
      // (un cliente que ya respondió no debe volver a "not_contacted" solo
      // porque se reimportó el archivo).
      const oppId = `opp-${actor.id}-${ENGINE_ID}`;
      const existing = await storage.getOpportunity(oppId);
      const opp = createOpportunity({
        id: oppId,
        actorId: actor.id,
        engineId: ENGINE_ID,
        companyName: actor.companyName,
        contactName: actor.contactName,
        priority,
        recoveryScore: score,
        estimatedValue,
        probability,
        windowDays,
        windowClosesAt: new Date(Date.now() + windowDays * 24 * 3600 * 1000).toISOString(),
        lastSignalLabel: `Renovación en ${daysUntilRenewal} días`,
        // Sin esto, createOpportunity() defaultea isDemo:true incluso para
        // datos reales (mismo hallazgo que crossSell.js, Sprint 12) — hereda
        // el isDemo del actor en vez de asumir uno.
        isDemo: actor.isDemo ?? false,
        ...(existing
          ? { responseStatus: existing.responseStatus, outcome: existing.outcome, notes: existing.notes }
          : {}),
      });
      await storage.saveOpportunity(opp);

      const knowledgeSnippets = await knowledge.getKnowledgeFor({
        actor, engineId: ENGINE_ID, purpose: "play_generation",
      });
      const play = await llm.generatePlay({
        actor, opportunity: opp, signals, engineId: ENGINE_ID,
        vertical: actor.vertical, knowledgeSnippets,
      });
      await storage.savePlay(play);
      return opp;
    },

    // Read APIs
    async listOpportunities(filter = {}) {
      return storage.listOpportunities({ ...filter, engineId: ENGINE_ID });
    },
    async getOpportunity(id) {
      const opp = await storage.getOpportunity(id);
      if (!opp || opp.engineId !== ENGINE_ID) return null;
      const actor = await storage.getActor(opp.actorId);
      const signals = await storage.listSignals(opp.actorId);
      const play = await storage.getLatestPlay(id);
      return { opportunity: opp, actor, signals, play };
    },

    // Mutations — idéntico a recovery.js (misma regla de idempotencia en "won").
    async recordOutcome(id, { result, notes, closedValueAmount }) {
      const previous = await storage.getOpportunity(id);
      const previousResult = previous?.outcome;
      const opp = await storage.updateOpportunity(id, { outcome: result, notes });
      const outcome = createOutcome({
        opportunityId: id,
        result,
        notes,
        closedValue: result === "won"
          ? { amount: closedValueAmount ?? opp.estimatedValue.value.amount, currency: "USD" }
          : undefined,
      });
      await storage.saveOutcome(outcome);
      if (result === "won" && previousResult !== "won") {
        const closure = createClosure({
          actorId: opp.actorId,
          companyName: opp.companyName,
          engineId: ENGINE_ID,
          closedValue: outcome.closedValue,
          closedByName: "Director Comercial",
        });
        await storage.saveClosure(closure);
        const team = await storage.listTeamMembers();
        const me = team.find((m) => m.id === "user-me");
        if (me) {
          await storage.saveTeamMember({
            ...me,
            monthClosedValue: {
              amount: me.monthClosedValue.amount + outcome.closedValue.amount,
              currency: "USD",
            },
            monthClosuresCount: me.monthClosuresCount + 1,
          });
        }
        crm.pushOutcome(outcome).catch(() => {});
      }
      return opp;
    },

    async regeneratePlay(opportunityId) {
      const opp = await storage.getOpportunity(opportunityId);
      const actor = await storage.getActor(opp.actorId);
      const signals = await storage.listSignals(opp.actorId);
      const knowledgeSnippets = await knowledge.getKnowledgeFor({
        actor, engineId: ENGINE_ID, purpose: "play_generation",
      });
      const play = await llm.generatePlay({
        actor, opportunity: opp, signals, engineId: ENGINE_ID,
        vertical: actor.vertical, knowledgeSnippets,
      });
      await storage.savePlay(play);
      return play;
    },

    async updatePlayBody(opportunityId, suggestedBody) {
      const current = await storage.getLatestPlay(opportunityId);
      if (!current) throw new Error("No play to update");
      const updated = { ...current, suggestedBody, generatedAt: new Date().toISOString() };
      await storage.savePlay(updated);
      return updated;
    },

    async getMetrics() {
      const opps = await storage.listOpportunities({ engineId: ENGINE_ID, limit: 10000 });
      const pending = opps.filter((o) => o.outcome === "pending");
      const won = opps.filter((o) => o.outcome === "won");
      const lost = opps.filter((o) => o.outcome === "lost");
      const closures = await storage.listClosures({ engineId: ENGINE_ID, limit: 10000 });
      const awakeValue = closures.reduce((s, c) => s + c.closedValue.amount, 0);
      const dormantValue = pending.reduce((s, o) => s + o.estimatedValue.value.amount, 0);
      const winRate =
        won.length + lost.length > 0 ? won.length / (won.length + lost.length) : 0;
      return {
        dormantValue: { amount: dormantValue, currency: "USD" },
        awakeValue: { amount: awakeValue, currency: "USD" },
        opportunitiesPending: pending.length,
        opportunitiesWon: won.length,
        opportunitiesLost: lost.length,
        winRate,
        avgWindowDays: Math.round(
          pending.reduce((s, o) => s + o.windowDays, 0) / Math.max(pending.length, 1)
        ),
        priorityBreakdown: {
          high: pending.filter((o) => o.priority === "high").length,
          medium: pending.filter((o) => o.priority === "medium").length,
          low: pending.filter((o) => o.priority === "low").length,
        },
      };
    },

    async getSummary() {
      const m = await this.getMetrics();
      const opps = await storage.listOpportunities({ engineId: ENGINE_ID, limit: 10000 });
      const actorsCount = new Set(opps.map((o) => o.actorId)).size;
      return {
        id: ENGINE_ID,
        label: "Renewal",
        question: "¿Qué contratos vencen y cuánto ingreso está en riesgo?",
        status: "live",
        potentialValue: {
          label: "commercial_value_estimated",
          value: m.dormantValue,
          confidence: 0.7,
          sampleSize: m.opportunitiesPending,
          errorMarginPct: 15,
          rationale:
            "Suma del valor estimado de contratos por renovar en el motor Renewal. " +
            "Se recalcula con cada outcome registrado.",
          lastRecalculatedAt: new Date().toISOString(),
        },
        actorsCount,
        opportunitiesCount: opps.length,
        recentClosedValueUsd: m.awakeValue.amount,
      };
    },
  };
}
